import { CommonModule } from '@angular/common';
import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ProjectInfrastructureService } from '@app/shared/services/project-infrastructure.service';
import { Project, PublicMeeting, PublicMeetingResponse } from 'lfx-pcc';
import { DateTime } from 'luxon';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { SkeletonModule } from 'primeng/skeleton';
import { Observable, switchMap, take } from 'rxjs';

import { CalendarComponent } from '../components/calendar/calendar.component';
import { CommitteeFilterComponent } from '../components/committee-filter/committee-filter.component';
import { ProjectsFilterComponent } from '../components/projects-filter/projects-filter.component';
import { TimelineComponent } from '../components/timeline/timeline.component';
import { MessageService } from 'primeng/api';
import { PCCService } from '@app/shared/services/pcc.service';
import { CACHE_PATTERNS } from '@app/shared/constants';
import { FeatureFlagService } from '@app/shared/services/feature-flag.service';

@Component({
  selector: 'lfx-meetings-overview',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    CalendarComponent,
    RouterModule,
    TimelineComponent,
    ProjectsFilterComponent,
    SkeletonModule,
    DividerModule,
    CommitteeFilterComponent,
  ],
  templateUrl: './meetings-overview.component.html',
  styleUrl: './meetings-overview.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MeetingsOverviewComponent {
  public meetings$: Observable<PublicMeetingResponse>;
  public meetings: WritableSignal<PublicMeeting[]> = signal([]);
  public pastMeetings$: Observable<PublicMeetingResponse>;
  public pastMeetings: WritableSignal<PublicMeeting[]> = signal([]);
  public project: WritableSignal<Partial<Project>>;
  public date: WritableSignal<Date>;
  public selectedCommittee: WritableSignal<string> = signal('');
  public showRefreshButton: Signal<boolean>;
  public hasProjectLogo: Signal<boolean>;

  private itxService: ProjectInfrastructureService = inject(ProjectInfrastructureService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private pccService: PCCService = inject(PCCService);
  private messageService: MessageService = inject(MessageService);
  private featureFlagService: FeatureFlagService = inject(FeatureFlagService);

  public constructor() {
    const filteredProjects = this.activatedRoute.snapshot.queryParams.projects?.split(',') || [];
    const committee = this.activatedRoute.snapshot.queryParams.committee;
    this.activatedRoute.queryParams.pipe(takeUntilDestroyed()).subscribe((params) => {
      if (params.committee) {
        this.selectedCommittee.set(params.committee);
      } else {
        this.selectedCommittee.set('');
      }
    });

    this.meetings$ = this.activatedRoute.params.pipe(switchMap((params) => this.itxService.getPublicMeetings(params.slug, filteredProjects, committee)));
    this.pastMeetings$ = this.itxService.pastMeetings$;
    this.project = this.itxService.project;
    const occurrence = this.activatedRoute.snapshot.queryParams.occurrence;

    // If the occurrence query parameter is present, set the date to the occurrence date.
    if (occurrence) {
      // Convert the occurrence ID to a date. It is an epoch timestamp.
      const date = DateTime.fromSeconds(parseInt(occurrence, 10));
      this.itxService.setDate(date.toJSDate());
    }

    this.date = this.itxService.date;

    // Initialize feature flag for refresh button
    this.showRefreshButton = this.featureFlagService.getBooleanFlag('refresh-public-meetings', false);

    // Initialize computed signal for project logo check
    this.hasProjectLogo = computed(() => {
      const proj = this.project();
      return !!(proj?.ProjectLogo && proj.ProjectLogo.trim().length > 0);
    });
  }

  public onRefreshPublicMeetings(slug: string): void {
    const cacheNames = [CACHE_PATTERNS.PUBLIC_MEETINGS(slug), CACHE_PATTERNS.PUBLIC_PAST_MEETINGS(slug), CACHE_PATTERNS.PUBLIC_PROJECT_COMMITTEES(slug)];

    this.pccService
      .clearCache(cacheNames)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.meetings$ = this.itxService.getPublicMeetings(slug, [], this.selectedCommittee() || '');
        },
        error: (error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Refresh Meetings',
            detail: `Failed to clear meetings cache: ${error.message || 'Unknown error'}`,
            key: 'support',
            data: error,
          });
        },
      });
  }

  public onImageError(event: Event): void {
    console.error('Failed to load project logo:', this.project().ProjectLogo, event);
    // Optionally, you could set a fallback image or hide the image element
  }
}
